import React from 'react';
import { 
  Home,
  Users,
  Image, 
  Type, 
  Palette, 
  FileText, 
  Monitor, 
  Printer, 
  BookOpen, 
  Sparkles,
  CreditCard,
  Globe,
  Shield,
  UserCog,
  Tags,
  Wrench,
  MessageCircle,
  Bug
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

interface SidebarProps {
  activeModule: string;
  onModuleChange: (module: string) => void;
  onBugReportClick: () => void; // New prop for bug report
}

export const Sidebar: React.FC<SidebarProps> = ({ activeModule, onModuleChange, onBugReportClick }) => {
  const { permissions } = useAuth();

  const mainMenuItems = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'people', label: 'People', icon: Users },
    { id: 'logos', label: 'Logos', icon: Image },
    { id: 'project-names', label: 'Project Names', icon: Type },
    { id: 'services', label: 'Services', icon: CreditCard },
    { id: 'domains', label: 'Domains', icon: Globe },
    { id: 'color-palettes', label: 'Color Palettes', icon: Palette },
    { id: 'print-templates', label: 'Print Templates', icon: Printer },
    { id: 'digital-templates', label: 'Digital Templates', icon: Monitor },
    { id: 'texts', label: 'Texts', icon: FileText },
    { id: 'wiki', label: 'Wiki', icon: BookOpen },
    { id: 'prompts', label: 'AI Prompts', icon: Sparkles },
  ];

  const adminMenuItems = [
    { 
      id: 'admin-users', 
      label: 'User Management', 
      icon: UserCog,
      requiresPermission: 'canManageUsers' // Available for admins and owners
    },
    { 
      id: 'admin-skills', 
      label: 'Skills Management', 
      icon: Wrench,
      requiresPermission: 'canManageUsers' // Available for admins and owners
    },
    { 
      id: 'admin-tags', 
      label: 'Tag Management', 
      icon: Tags,
      requiresPermission: 'canManageUsers' // Available for admins and owners
    },
    { 
      id: 'admin-whitelist', 
      label: 'Whitelist Management', 
      icon: Shield,
      requiresPermission: 'canManageWhitelist' // Owner-only
    },
  ];

  const visibleAdminItems = adminMenuItems.filter(item => 
    permissions[item.requiresPermission as keyof typeof permissions]
  );

  return (
    <div className="w-64 bg-card border-r min-h-screen p-6">
      <div className="mb-8">
        <h2 className="text-lg font-semibold">Gule Sider</h2>
      </div>
      
      <nav className="space-y-6">
        {/* Main Navigation */}
        <div className="space-y-2">
          {mainMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeModule === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => onModuleChange(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                  isActive 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Suggestion Box & Bug Report - Bottom section */}
        <div className="pt-4 border-t border-border space-y-2">
          <button
            onClick={() => onModuleChange('suggestion-box')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
              activeModule === 'suggestion-box'
                ? 'bg-primary text-primary-foreground' 
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            }`}
          >
            <MessageCircle size={20} />
            <span>Suggestion Box</span>
          </button>
          
          <button
            onClick={onBugReportClick}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors text-muted-foreground hover:bg-red-50 hover:text-red-600"
          >
            <Bug size={20} />
            <span>Report a Bug</span>
          </button>
        </div>

        {/* Admin Section */}
        {visibleAdminItems.length > 0 && (
          <div className="space-y-1 pt-4 border-t border-border">
            {visibleAdminItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeModule === item.id;
              
              return (
                <button
                  key={item.id}
                  onClick={() => onModuleChange(item.id)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-left transition-colors text-sm ${
                    isActive 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <Icon size={16} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </nav>
    </div>
  );
};